<template>
  <div class="deadlines-page">
    <header class="page-heading">
      <div>
        <p>Aujourd’hui</p>
        <h1>Échéances</h1>
        <span
          >Surveillez les rappels programmés avant la fin de vos annonces.</span
        >
      </div>
      <button
        type="button"
        :disabled="loading"
        :aria-label="loading ? 'Actualisation…' : 'Actualiser'"
        @click="loadJobs()"
      >
        <span :class="{ spinning: loading }">↻</span
        ><span class="refresh-label">{{
          loading ? "Actualisation…" : "Actualiser"
        }}</span>
      </button>
    </header>
    <section class="summary">
      <div>
        <strong>{{ jobs.length }}</strong
        ><span>Annonces avec alertes</span>
      </div>
      <div>
        <strong>{{ nextAlertLabel }}</strong
        ><span>Prochaine alerte</span>
      </div>
      <div>
        <strong>{{ urgentAlertsCount }}</strong
        ><span>Finissent dans l’heure</span>
      </div>
      <div>
        <strong>{{ todayAlertsCount }}</strong
        ><span>Finissent aujourd’hui</span>
      </div>
    </section>
    <div v-if="error" class="error" role="alert">{{ error }}</div>
    <div v-if="loading" class="loading">
      <LoaderDiv
        :isLoading="true"
        variant="modern"
        loaderText="Chargement des échéances…"
      />
    </div>
    <div v-else-if="!jobs.length" class="empty">
      <span>◷</span>
      <h2>Aucune échéance programmée</h2>
      <p>Les rappels de vos annonces suivies apparaîtront ici.</p>
      <router-link to="/parse">Suivre une annonce</router-link>
    </div>
    <main v-else class="groups">
      <section v-for="group in groupedJobs" :key="group.key" class="group">
        <header>
          <div>
            <h2>{{ group.label }}</h2>
            <p>{{ group.description }}</p>
          </div>
          <span>{{ group.jobs.length }}</span>
        </header>
        <div class="jobs">
          <article
            v-for="job in group.jobs"
            :key="job.offer_id || job.job_id"
            :class="urgencyClass(job)"
          >
            <button type="button" class="identity" @click="showDetails(job)">
              <span class="thumb"
                ><img
                  :src="imageUrl(job.offer_id)"
                  :alt="job.title || 'Annonce'"
                  @error="handleImageError" /></span
              ><span
                ><strong>{{ job.title || "Annonce sans titre" }}</strong
                ><small>#{{ job.offer_id }}</small
                ><small v-if="job.cote" class="cote-line"
                  ><CoteChip :cote="job.cote" /></small
              ></span>
            </button>
            <!-- Time left before the END of the auction, counting down;
                 the next alert below -->
            <div class="schedule">
              <small v-if="!isEnded(job)" class="caption">fin dans</small
              ><strong>{{ countdown(job) }}</strong
              ><small>fin {{ formatDate(endOf(job)) }}</small
              ><small v-if="nextAlertText(job)" class="alerts next-alert">{{
                nextAlertText(job)
              }}</small
              ><small v-if="job.alerts?.length > 1" class="alerts">{{
                alertsLabel(job)
              }}</small>
            </div>
            <span :class="['priority', urgencyClass(job)]">{{
              priorityLabel(job)
            }}</span
            ><span class="channel">Notification</span
            ><button
              v-if="!isEnded(job)"
              type="button"
              class="cancel"
              @click="askCancel(job)"
            >
              Annuler
            </button>
          </article>
        </div>
      </section>
    </main>
    <OfferQuickPanel
      :visible="panelOpen"
      :offer="selectedOffer"
      :reminders="selectedJob ? remindersText(selectedJob) : ''"
      :can-cancel-reminders="Boolean(selectedJob) && !isEnded(selectedJob)"
      @cancel-reminders="askCancel(selectedJob)"
      @close="panelOpen = false"
      @refresh="refreshOffer"
      @price-updated="(fields) => Object.assign(selectedOffer, fields)"
    /><Teleport to="body"
      ><div
        v-if="jobToCancel"
        class="confirm-layer"
        @click.self="jobToCancel = null"
      >
        <section class="confirm" role="alertdialog" aria-modal="true">
          <p>Rappel</p>
          <h2>
            {{
              jobToCancel.alerts?.length > 1
                ? "Annuler les alertes de cette annonce ?"
                : "Annuler cette alerte ?"
            }}
          </h2>
          <span>{{
            jobToCancel.alerts?.length > 1
              ? `Les ${jobToCancel.alerts.length} notifications prévues ne seront plus envoyées.`
              : "La notification ne sera plus envoyée pour cette annonce."
          }}</span>
          <footer>
            <button type="button" @click="jobToCancel = null">Conserver</button
            ><button
              type="button"
              class="danger"
              :disabled="cancelling"
              @click="cancelJob"
            >
              {{ cancelling ? "Annulation…" : "Annuler le rappel" }}
            </button>
          </footer>
        </section>
      </div></Teleport
    >
  </div>
</template>
<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { formatRemaining, parseTime, useNow } from "../clock";
import axios from "axios";
import { useRouter } from "vue-router";
import { isTokenExpired } from "../auth";
import { parse } from "@/common.js";
import config from "@/config/config.js";
import LoaderDiv from "./LoaderDiv.vue";
import OfferQuickPanel from "./offers/OfferQuickPanel.vue";
import CoteChip from "./common/CoteChip.vue";
const HOUR = 3600000,
  DAY = 24 * HOUR;
export default {
  name: "JobsList",
  components: { CoteChip, LoaderDiv, OfferQuickPanel },
  setup() {
    const router = useRouter(),
      jobs = ref([]),
      loading = ref(true),
      error = ref(""),
      selectedOffer = ref({}),
      selectedJob = ref(null),
      panelOpen = ref(false),
      jobToCancel = ref(null),
      cancelling = ref(false);
    // The clock ticks every second: the times below count down on their own
    const now = useNow(1000);
    const time = parseTime,
      // End of the auction (the API gives it; the next alert when missing)
      endOf = (job) => job.end_time || job.notify_time,
      left = (job) => time(endOf(job)) - now.value,
      isEnded = (job) => left(job) <= 0,
      sortedJobs = computed(() =>
        [...jobs.value].sort((a, b) => time(endOf(a)) - time(endOf(b))),
      ),
      upcoming = computed(() =>
        sortedJobs.value.filter((job) => !isEnded(job)),
      ),
      urgentAlertsCount = computed(
        () => upcoming.value.filter((job) => left(job) <= HOUR).length,
      ),
      todayAlertsCount = computed(
        () =>
          upcoming.value.filter(
            (job) =>
              new Date(time(endOf(job))).toDateString() ===
              new Date(now.value).toDateString(),
          ).length,
      ),
      // Alerts of an auction still to be sent
      pendingAlerts = (job) =>
        (job.alerts?.length
          ? job.alerts
          : [{ notify_time: job.notify_time }]
        ).filter((alert) => time(alert.notify_time) > now.value),
      nextAlertLabel = computed(() => {
        const next = jobs.value
          .flatMap((job) => pendingAlerts(job))
          .map((alert) => time(alert.notify_time))
          .sort((a, b) => a - b)[0];
        return next ? formatRemaining(next, now.value) : "Aucune";
      }),
      groupedJobs = computed(() => {
        const groups = [
            {
              key: "urgent",
              label: "Imminentes",
              description: "Fin dans moins d’une heure",
              jobs: [],
            },
            {
              key: "today",
              label: "Aujourd’hui",
              description: "Fin plus tard dans la journée",
              jobs: [],
            },
            {
              key: "tomorrow",
              label: "Demain",
              description: "Fin demain",
              jobs: [],
            },
            {
              key: "later",
              label: "Plus tard",
              description: "Échéances suivantes",
              jobs: [],
            },
            {
              key: "ended",
              label: "Terminées",
              description:
                "Depuis l’ouverture de la page (« Actualiser » pour les retirer)",
              jobs: [],
            },
          ],
          today = new Date(now.value),
          tomorrow = new Date(now.value);
        tomorrow.setDate(tomorrow.getDate() + 1);
        sortedJobs.value.forEach((job) => {
          const date = new Date(time(endOf(job)));
          if (isEnded(job)) groups[4].jobs.push(job);
          else if (left(job) <= HOUR) groups[0].jobs.push(job);
          else if (date.toDateString() === today.toDateString())
            groups[1].jobs.push(job);
          else if (date.toDateString() === tomorrow.toDateString())
            groups[2].jobs.push(job);
          else groups[3].jobs.push(job);
        });
        return groups.filter((group) => group.jobs.length);
      });
    const headers = () => ({
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      }),
      // silent: refresh in place (every minute, back to the tab): no
      // "Chargement…", and an auction that has just ended stays on screen
      // (marked "Terminée") until a manual refresh
      loadJobs = async ({ silent = false } = {}) => {
        const token = localStorage.getItem("access_token");
        if (isTokenExpired(token)) {
          if (!silent) router.push("/");
          return;
        }
        if (!silent) loading.value = true;
        error.value = "";
        try {
          const response = await axios.get(`${config.apiUrl}/jobs`, {
            headers: headers(),
          });
          const fresh = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.result)
              ? response.data.result
              : [];
          if (silent) {
            const keyOf = (job) => job.offer_id || job.job_id;
            const freshKeys = new Set(fresh.map(keyOf));
            const endedKept = jobs.value.filter(
              (job) => !freshKeys.has(keyOf(job)) && isEnded(job),
            );
            jobs.value = [...fresh, ...endedKept];
          } else jobs.value = fresh;
        } catch (requestError) {
          console.error("Chargement des alertes impossible", requestError);
          if (!silent)
            error.value =
              "Impossible de charger les échéances. Veuillez réessayer.";
        } finally {
          loading.value = false;
        }
      };
    const countdown = (job) => formatRemaining(endOf(job), now.value),
      nextAlertText = (job) => {
        if (isEnded(job)) return "";
        const next = pendingAlerts(job)[0];
        return next
          ? `alerte dans ${formatRemaining(next.notify_time, now.value)}`
          : "alertes envoyées";
      },
      urgencyClass = (job) => {
        const diff = left(job);
        return diff <= 0
          ? "ended"
          : diff <= HOUR
            ? "urgent"
            : diff <= DAY
              ? "today"
              : "normal";
      },
      priorityLabel = (job) =>
        ({
          ended: "Terminée",
          urgent: "Critique",
          today: "Aujourd’hui",
        })[urgencyClass(job)] || "Planifiée",
      formatDate = (value) =>
        new Intl.DateTimeFormat("fr-FR", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(time(value))),
      imageUrl = (id) => `${config.cdnUrl}/images/${id}/${id}_thumbnail.jpg`,
      handleImageError = (event) => {
        event.target.style.visibility = "hidden";
      };
    const showDetails = async (job) => {
        selectedJob.value = job;
        selectedOffer.value = {
          _key: job.offer_id,
          title: job.title,
          endtime: job.endtime || job.notify_time,
          source: job.source || "yahoo",
        };
        panelOpen.value = true;
        try {
          const response = await axios.get(
            `${config.apiUrl}/offers/${job.offer_id}`,
            { headers: headers() },
          );
          selectedOffer.value =
            response.data?.result || response.data || selectedOffer.value;
        } catch (requestError) {
          console.error("Détail de l’annonce indisponible", requestError);
        }
      },
      refreshOffer = async (offer) => {
        try {
          await parse(offer);
        } catch (requestError) {
          console.error("Actualisation impossible", requestError);
        }
      },
      // Alerts of an auction, e.g. "Alertes 1 h, 15 min et 5 min avant la fin"
      // "5 min avant la fin", "1 h, 15 min et 5 min avant la fin"
      remindersText = (job) => {
        const labels = (job.alerts?.length ? job.alerts : [job]).map(
          (alert) =>
            alert.minutes === 60 ? "1 h" : `${alert.minutes || 5} min`,
        );
        const listed =
          labels.length > 1
            ? `${labels.slice(0, -1).join(", ")} et ${labels.at(-1)}`
            : labels[0];
        return `${listed} avant la fin`;
      },
      alertsLabel = (job) => {
        const labels = job.alerts.map((alert) =>
          alert.minutes === 60 ? "1 h" : `${alert.minutes} min`,
        );
        return `Alertes ${labels.slice(0, -1).join(", ")} et ${labels.at(
          -1,
        )} avant la fin`;
      },
      askCancel = (job) => {
        jobToCancel.value = job;
      },
      cancelJob = async () => {
        if (!jobToCancel.value) return;
        cancelling.value = true;
        try {
          // One row per auction: every alert of the auction is cancelled
          const alertIds = jobToCancel.value.alerts?.length
            ? jobToCancel.value.alerts.map((alert) => alert.job_id)
            : [jobToCancel.value.job_id];
          for (const alertId of alertIds) {
            await axios.post(
              `${config.apiUrl}/jobs/${alertId}/cancel`,
              {},
              { headers: headers() },
            );
          }
          jobs.value = jobs.value.filter(
            (job) => job.job_id !== jobToCancel.value.job_id,
          );
          // Cancelled from its sheet: the sheet closes with it
          if (selectedJob.value === jobToCancel.value) {
            panelOpen.value = false;
            selectedJob.value = null;
          }
          jobToCancel.value = null;
        } catch (requestError) {
          console.error("Annulation impossible", requestError);
          error.value = "Impossible d’annuler ce rappel.";
        } finally {
          cancelling.value = false;
        }
      };
    onMounted(() => loadJobs());
    // Data refreshed every minute while the page is shown, and when coming
    // back to the browser tab (an end time can move: Yahoo extends an auction
    // after a late bid, Prexens reads the listing again before the alert)
    const REFRESH_MS = 60 * 1000;
    const refreshTimer = setInterval(() => {
      if (document.visibilityState === "visible") loadJobs({ silent: true });
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") loadJobs({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    onUnmounted(() => {
      clearInterval(refreshTimer);
      document.removeEventListener("visibilitychange", onVisible);
    });
    // A guide computed in a sheet: the rows of this offer are updated
    const onChanged = (event) => {
      const { type, record } = event.detail || {};
      if (type !== "offer" || !record?._key || !("cote" in record)) return;
      jobs.value
        .filter((job) => job.offer_id === record._key)
        .forEach((job) => (job.cote = record.cote));
    };
    window.addEventListener("prexens:changed", onChanged);
    onUnmounted(() => window.removeEventListener("prexens:changed", onChanged));
    return {
      remindersText,
      selectedJob,
      alertsLabel,
      askCancel,
      cancelJob,
      cancelling,
      countdown,
      endOf,
      error,
      isEnded,
      formatDate,
      groupedJobs,
      handleImageError,
      imageUrl,
      jobToCancel,
      jobs,
      loadJobs,
      loading,
      nextAlertLabel,
      nextAlertText,
      panelOpen,
      priorityLabel,
      refreshOffer,
      selectedOffer,
      showDetails,
      todayAlertsCount,
      urgencyClass,
      urgentAlertsCount,
    };
  },
};
</script>
<style scoped>
.deadlines-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
  font-family: "DM Sans", sans-serif;
}
.page-heading,
.summary,
.groups,
.error,
.loading,
.empty {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading > div > span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.page-heading > button {
  display: flex;
  height: 40px;
  align-items: center;
  gap: 7px;
  padding: 0 14px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #566276;
  font-size: 11px;
  font-weight: 800;
}
.spinning {
  animation: spin 0.7s linear infinite;
}
.summary {
  display: flex;
  margin-bottom: 20px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.summary div {
  display: flex;
  min-width: 180px;
  flex: 1;
  flex-direction: column;
  padding: 16px 20px;
  border-right: 1px solid var(--line);
}
.summary div:last-child {
  border: 0;
}
.summary strong {
  font-size: 20px;
}
.summary span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 11px;
}
.error {
  margin-bottom: 14px;
  padding: 12px 15px;
  border: 1px solid #eccfcb;
  border-radius: 8px;
  background: #fff5f4;
  color: #a4453f;
  font-size: 12px;
}
.loading {
  min-height: 400px;
}
.empty {
  display: flex;
  min-height: 400px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: 11px;
  background: #fff;
  text-align: center;
}
.empty > span {
  font-size: 28px;
  color: var(--green);
}
.empty h2 {
  margin: 13px 0 6px;
  font-size: 17px;
}
.empty p {
  margin: 0 0 17px;
  color: var(--muted);
  font-size: 12px;
}
.empty a {
  padding: 11px 14px;
  border-radius: 8px;
  background: #172235;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  text-decoration: none;
}
.groups {
  display: grid;
  gap: 18px;
}
.group > header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  margin-bottom: 9px;
  padding: 0 3px;
}
.group h2 {
  margin: 0;
  font-size: 15px;
}
.group header p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: var(--meta-size);
}
.group header > span {
  display: grid;
  min-width: 25px;
  height: 25px;
  place-items: center;
  border-radius: 13px;
  background: #eaf7f2;
  color: var(--green);
  font-size: var(--meta-size);
  font-weight: 800;
}
.jobs {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 11px;
  background: #fff;
}
.jobs article {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 165px 100px 105px 75px;
  align-items: center;
  gap: 14px;
  padding: 13px 16px;
  border-bottom: 1px solid #edf0f4;
}
.jobs article:last-child {
  border: 0;
}
.jobs article.urgent {
  box-shadow: inset 3px 0 #c84d46;
}
.jobs article.today {
  box-shadow: inset 3px 0 #d39235;
}
.identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
  border: 0;
  background: none;
  text-align: left;
}
.thumb {
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #e1e6ec;
  border-radius: 7px;
  background: #f1f4f7;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.identity > span:last-child {
  min-width: 0;
}
.identity strong,
.identity small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.identity strong {
  font-size: 12px;
}
.identity small.cote-line {
  overflow: visible;
  white-space: normal;
}
.identity small {
  margin-top: 4px;
  color: var(--muted);
  font-size: var(--meta-size);
}
.schedule {
  display: flex;
  flex-direction: column;
}
.schedule strong {
  font-size: 13px;
}
.schedule small {
  margin-top: 4px;
  color: var(--muted);
  font-size: var(--meta-size);
}
.schedule small.caption {
  margin: 0 0 1px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.schedule small.alerts {
  margin-top: 2px;
}
.priority {
  width: max-content;
  padding: 5px 7px;
  border-radius: 6px;
  background: #f0f2f4;
  color: #667283;
  font-size: var(--meta-size);
  font-weight: 800;
}
.jobs article.ended {
  opacity: 0.6;
}
.priority.ended {
  background: #eef1f5;
  color: #6f7b8d;
}
.priority.urgent {
  background: #fff0ef;
  color: #b04742;
}
.priority.today {
  background: #fff3e5;
  color: #a96316;
}
.channel {
  color: #697688;
  font-size: var(--meta-size);
}
.cancel {
  height: 32px;
  border: 1px solid #d8dee6;
  border-radius: 7px;
  background: #fff;
  color: #677386;
  font-size: var(--meta-size);
  font-weight: 700;
}
.confirm-layer {
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: grid;
  place-items: center;
  padding: calc(20px + var(--safe-top)) 20px calc(20px + var(--safe-bottom));
  background: rgba(15, 23, 36, 0.48);
}
.confirm {
  width: min(420px, 100%);
  padding: 23px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 25px 70px rgba(15, 23, 36, 0.25);
}
.confirm > p {
  margin: 0 0 7px;
  color: #b04742;
  font-size: var(--meta-size);
  font-weight: 800;
  text-transform: uppercase;
}
.confirm h2 {
  margin: 0;
  font-size: 18px;
}
.confirm > span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 11px;
}
.confirm footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
}
.confirm footer button {
  height: 38px;
  padding: 0 13px;
  border: 1px solid #d5dce5;
  border-radius: 7px;
  background: #fff;
  font-size: 11px;
  font-weight: 800;
}
.confirm footer .danger {
  border: 0;
  background: #b04742;
  color: #fff;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (max-width: 1050px) {
  .jobs article {
    grid-template-columns: minmax(240px, 1fr) 135px 90px 70px;
  }
  .channel {
    display: none;
  }
}
@media (max-width: 760px) {
  .deadlines-page {
    padding: 20px 14px;
  }
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .summary div {
    min-width: 0;
    padding: 13px;
  }
  .summary div:nth-child(4) {
    display: none;
  }
  .jobs article {
    grid-template-columns: minmax(200px, 1fr) 90px 65px;
  }
  .priority {
    display: none;
  }
  .schedule small:not(.caption):not(.next-alert) {
    display: none;
  }
  .identity strong {
    white-space: normal;
  }
  .page-heading h1 {
    font-size: 26px;
  }
}
@media (max-width: 460px) {
  /* Phone: refresh button as an icon next to the title, as on the
     dashboard (Vue d'ensemble) */
  .page-heading {
    position: relative;
    padding-right: 52px;
  }
  .page-heading > button {
    position: absolute;
    top: 0;
    right: 0;
    width: 40px;
    padding: 0;
    justify-content: center;
  }
  .page-heading .refresh-label {
    display: none;
  }
}
@media (max-width: 500px) {
  .summary div:nth-child(3) {
    display: none;
  }
  .jobs article {
    grid-template-columns: minmax(0, 1fr) 96px;
  }
  /* Phone: no room in the row; reminders are cancelled from the sheet */
  .cancel {
    display: none;
  }
  .thumb {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
  }
}
</style>
